import { Box, Button, LocationInput, Stack, toast } from '@montugroup/design-system';
import type { PlaceDetails } from '@montugroup/design-system/LocationInput';
import { styled } from '@mui/material';
import { useEffect, useState } from 'react';

import ManualAddressForm from '@/components/order/order-address-form/ManualAddressForm';
import settings from '@/constants/constants';
import { FF_ENABLE_GOOGLE_PLACES_UMEDS } from '@/constants/featureFlags';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import useUpdateUserProfile from '@/hooks/user/useUpdateUserProfile';
import { Logger } from '@/utils/logger';
import { useAddressForm, useSession } from '@montugroup/circuit-shared';
import AuthService from '@montugroup/circuit-shared/services/authentication.service';
import type { IUserProfileResponse, LoginUser } from '@montugroup/circuit-shared/types/user';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { formatAddressString, populateAddressFormIfExists } from '@/utils/address';
import { useAddressValidation } from '@montugroup/circuit-shared/hooks/user/useAddressValidation';
import type { Address, AddressDataType } from '@montugroup/circuit-shared/types/address';

const logger = new Logger('ShippingAddressForm');

export const ButtonBox = styled(Box)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.up('lg')]: {
    maxWidth: '50%',
    paddingRight: theme.spacing(3),
  },
}));

function ShippingAddressForm({
  userData,
  getUserState,
}: {
  userData: IUserProfileResponse | undefined;
  getUserState: () => string | null;
}) {
  const { session } = useSession();
  const { mutate, isPending } = useUpdateUserProfile();
  const shouldBlockExternalTerritoryPostcodes = useFeatureFlag('enable_blocking_external_territories');
  const { flags } = useFeatureFlags();
  const isGooglePlacesEnabled = flags[FF_ENABLE_GOOGLE_PLACES_UMEDS];
  const [enableManualAddress, setEnableManualAddress] = useState(false);
  const [error, setError] = useState<string>('');

  const formMethods = useAddressForm({ shouldBlockExternalTerritoryPostcodes });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = formMethods;

  const address = formMethods.getValues();

  const { validateStreetNumber, validatePostcodeNotBlockedFromDelivery } = useAddressValidation();

  useEffect(() => {
    populateAddressFormIfExists(formMethods, userData?.user, getUserState);
  }, [userData, getUserState]);

  const handleAddressUpdate = async (updatedAddressValues?: Address) => {
    const updatedAddress = updatedAddressValues ?? formMethods.getValues();
    const stateId = userData?.states.find((state) => state.name === updatedAddress.state)?.id;
    const payload = {
      user: {
        address: `${updatedAddress.addressLine1}$$$$${updatedAddress.addressLine2}`,
        city: updatedAddress.suburb,
        state_id: stateId,
        zip_code: updatedAddress.postcode,
      },
    };

    mutate(payload, {
      onSuccess: () => {
        toast.success('Shipping Address Updated');
        setEnableManualAddress(false);
        // TODO: I don't know if the following localStorage updates are still required here but adding in for non-breaking/backwards compatibility with old logic
        if (session?.user) {
          const updatedUser: LoginUser = {
            ...session.user,
            ...payload.user,
            id: session.user.id,
            state_id: payload.user.state_id ?? session.user.state_id,
          };
          AuthService.updateUser(updatedUser);
        }
      },
      onError: (error) => {
        toast.error('Something went wrong, please try again');
        logger.error('Failed to update user profile address', error);
      },
    });
  };

  const handleSelectedAddress = async (addressData: AddressDataType) => {
    const placeDetails: PlaceDetails = {
      address_components: [
        {
          long_name: addressData.street_number,
          short_name: addressData.street_number,
          types: ['street_number'],
        },
      ],
    };

    const streetNumberValidationResult = validateStreetNumber(placeDetails);

    if (!streetNumberValidationResult.isValid) {
      setError(`${streetNumberValidationResult.errorMessageTitle} ${streetNumberValidationResult.errorMessage}`);
      return;
    }

    if (shouldBlockExternalTerritoryPostcodes) {
      const postcodeBlockedValidationResult = validatePostcodeNotBlockedFromDelivery(addressData.postcode);
      if (!postcodeBlockedValidationResult.isValid) {
        postcodeBlockedValidationResult.errorMessage && setError(postcodeBlockedValidationResult.errorMessage);
        return;
      }
    }

    const formattedAddress = [addressData.subpremise, addressData.street_number, addressData.address]
      .filter(Boolean)
      .join(' ');

    const updatedAddress: Address = {
      addressLine1: formattedAddress,
      addressLine2: '',
      state: addressData.state,
      suburb: addressData.suburb || addressData.city,
      postcode: addressData.postcode,
    };

    formMethods.setValue('addressLine1', String(updatedAddress.addressLine1));
    formMethods.setValue('addressLine2', String(updatedAddress.addressLine2));
    formMethods.setValue('postcode', String(updatedAddress.postcode));
    formMethods.setValue('state', String(updatedAddress.state));
    formMethods.setValue('suburb', String(updatedAddress.suburb));
  };

  const handleResetAddress = () => {
    formMethods.reset();
    setError('');
  };

  const handleManualAddressClick = () => {
    setEnableManualAddress(true);
  };

  return (
    <Box maxWidth={{ xs: '100%', lg: '66%' }}>
      {isGooglePlacesEnabled && !enableManualAddress ? (
        <Box marginTop={6} component="form" role="form" onSubmit={handleSubmit(handleAddressUpdate)}>
          <Stack spacing={6}>
            <LocationInput
              value={formatAddressString(address)}
              name="fullAddress"
              label="Shipping Address"
              shrinkLabel
              id="location-input"
              googlePlacesApiKey={settings.googlePlacesApiKey}
              error={error}
              onSelectedAddress={async (addressData) => {
                await handleSelectedAddress(addressData);
              }}
              onReset={handleResetAddress}
              manualAddressFn={handleManualAddressClick}
              editIcon
              variant="outlined"
            />
            <ButtonBox>
              <Button type="submit" disabled={isSubmitting || isPending} size="large" variant="contained" fullWidth>
                Update shipping address
              </Button>
            </ButtonBox>
          </Stack>
        </Box>
      ) : (
        <ManualAddressForm
          onSubmit={handleAddressUpdate}
          states={userData?.states || []}
          formMethods={formMethods}
          variant="standard"
          isLoading={isPending}
        />
      )}
    </Box>
  );
}

export default ShippingAddressForm;
